import defaultBrokenImageURL from '../assets/brokenImage.png';
import { defaultDataFormOptions } from './dataFormOptions';
import { defaultUILib } from './uiLib';
import { devDoc } from './devDoc';
import { defaultUploader } from './uploader';
import { defaultDesignerConfig } from './designerConfig';
import { defaultDesignerUiConfig } from "../../lib/defaults/designerUiConfig";

/** 这些是一些默认值 */
export const defaultConfig = {
  /**
   * UICore Designer 不是每次修改都会发出 update:pagelets 事件，而是：
   *
   * - 很确定的特定情况，比如拖拽组件，是低频且必须的，会发出
   * - 发生了修改后一段时间，自动会发出
   *
   * 这个选项是控制第二个情况的时间长度
   */
  designerCommitDebounceDuration: 3000,
  /**
   * 用于设计模式，表达式输入框的样式配置，比如默认主题之类的
   *
   * 配置参见 CodeMirror 5 的文档，以及，如果引入了什么东西（比如主题），记得对应 import 样式
   */
  cmOptions: {
    theme: 'idea'
  },
  /**
   * （设计模式用）在需要基础的布局组件（行、纵向容器）时，使用哪个组件
   *
   * value 支持是组件类型（字符串），或者 Layout 对象（可以携带一些 props，但是 children 以及部分 props 会被覆盖）
   */
  baseLayoutComponents: {
    paragraph: 'w-paragraph',
    block: 'w-container',
    scopedSlotRoot: {
      type: 'w-container'
    }
  },
  /**
   * 在这里可以更换物料组件的分组信息
   *
   * @type {UICore.MaterialGroup[]}
   */
  categories: [{
    name: '布局',
    order: 1
  }, {
    name: '按钮',
    order: 2
  }, {
    name: '展示',
    order: 3
  }

  // 其他自动创建的分组，order 都是 1000
  // 如果要把某个分组挪到很靠后的位置，请将 order 设置为大于 1000 的数字
  ],
  /**
   * 破图替换，如果图片挂了会显示这个 URL
   */
  brokenImageReplacement: defaultBrokenImageURL,
  /**
   * 组件属性面板的 dataSchema ContextOptions，一般不用管
   *
   * @type {import('@tencent/data-schema/lib/vue-ui/DataFormContext').DataFormContextOptions}
   */
  propsDSOptions: defaultDataFormOptions,
  /**
   * 定义了一些常见的 UI 行为（例如 alert、confirm 等）
   */
  uiLib: defaultUILib,
  /**
   * （设计模式用）如果一个组件宽度或者高度小于这个值，就会强行撑起来，避免看不见或者选中不了
   */
  minComponentMeasurement: 18,
  /**
   * （设计模式用）（Form插件）拖出组件时是否使用 w-form-unsettled-editor
   */
  useUnsettledEditor: false,
  /**
   * 页面内表单的默认配置信息，例如，可以配置 `layout: "vertical"` 使得默认的表单项的布局不一样
   *
   * 仅限初始化的时候配置，后续配置修改不一定生效
   *
   * @type {Record<string,any>}
   */
  rootFormProps: {},
  /**
   * 文件上传功能。此功能将会影响内置的“文件上传”组件，以及样式面板等具备上传功能的地方。
   *
   * 你可以编写一个自己的 async function，作为 fileUploadHandler
   *
   * - 输入为 `UICore.UploadRequest`
   * - 返回为 `Promise<URL字符串>`
   * - 如果上传失败，请直接 throw 错误
   *
   * 一个示例：
   *
   * ```js
   * async (req) => {
   *   const formData = new FormData();
   *   formData.append("file", req.file);
   *   formData.append("prefix", req.prefix || "");
   *
   *   const res = await axios.request({
   *       method: "post",
   *       url: "/aaa",
   *       data: formData,
   *       onUploadProgress: (p) => {
   *         if (req.onProgress) req.onProgress(p.loaded, p.total);
   *       }
   *   });
   *
   *   return res.data.url;
   * }
   * ```
   */
  fileUploadHandler: defaultUploader,
  /** 是否开启文档 */
  docEnable: true,
  docLink: devDoc,
  designerConfig: defaultDesignerConfig,
  /** 提供一个key来标识当前的[用户-页面] 当前页面的所有ls变量都会存储在这个Key下面*/
  userCacheKey: 'uicore:lsCache',
  // 能够对uicore默认的ui组合进行定制
  designerUiConfig: defaultDesignerUiConfig,
  // 可以关闭uicore自带的datasourcePlugin
  statePlugin: true,
  // 运行时根据组件依赖来重渲染
  ucFastMode: false,
  // 设为true时 uc-renderer将不会响应输入的pagelet中的深层变化 有一定的性能提升
  ucStaticRenderer: false,
  flowErrorLogger: {
    error: console.error
  },
  /**
   * 组件样式
   * @type {Record<string,import('~/designer/StylePanelState').CSSObject> | undefined}
   */
  componentStyles: undefined
};